import { useState, useEffect } from 'react';
import { useSearchParam } from 'react-use';

// Global state to track which modal is currently open
let globalModalState: string | null = null;
let globalModalSetters: Set<(isOpen: boolean) => void> = new Set();

export const useFormModal = (formHandle: string | string[]) => {
  const openFormParam = useSearchParam('openForm');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  
  // Handle both single form handle and array of handles
  const handles = Array.isArray(formHandle) ? formHandle : [formHandle];
  const shouldOpen = openFormParam && handles.includes(openFormParam);

  useEffect(() => {
    // Register this setter
    globalModalSetters.add(setIsOpen);
    
    // Check if this modal should open based on URL parameter
    if (shouldOpen && !globalModalState) {
      globalModalState = openFormParam;
      setIsOpen(true);
      
      // Close all other modals
      globalModalSetters.forEach(setter => {
        if (setter !== setIsOpen) {
          setter(false);
        }
      });
    }

    return () => {
      // Cleanup on unmount
      globalModalSetters.delete(setIsOpen);
      if (globalModalState === openFormParam) {
        globalModalState = null;
      }
    };
  }, [shouldOpen, openFormParam]);

  const openModal = () => {
    // Close all other modals first
    globalModalSetters.forEach(setter => {
      if (setter !== setIsOpen) {
        setter(false);
      }
    });
    
    globalModalState = handles[0]; // Use first handle as the active one
    setIsOpen(true);
  };

  const closeModal = () => {
    globalModalState = null;
    setIsOpen(false);
  };

  return {
    isOpen,
    openModal,
    closeModal,
  };
};
